// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import HistoryContext from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getNotifyStyle } from "@plumberssupply/App/Components/Notify";
import InvoiceSummaryCard, { InvoiceSummaryCardStyles } from "@plumberssupply/App/Components/OpenInvoices/InvoiceSummaryCard";
import TitleTypography, { TitleTypographyProps } from "@plumberssupply/App/Components/TitleTypography/TitleTypography";
import DataTable, { DataTableProps } from "@plumberssupply/MobiusOverrides/DataTable/DataTable";
import IconArrowRight from "@plumberssupply/XDL/Icons/IconArrowRight";
import { color, dashboardTablesContainerStyles, xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const enum fields {
    numberOfRecords = "numberOfRecords",
    wrapperBorders = "wrapperBorders",
    payInvoicesLink = "payInvoicesLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.wrapperBorders]: boolean;
        [fields.payInvoicesLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        invoicesDataView: getInvoicesDataView(state, openInvoicesParameter),
        invoicesHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        canViewInvoices: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        invoiceDetailsPageLink: getPageLinkByPageType(state, "InvoiceDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    loadInvoices,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenInvoicesStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noInvoicesFoundGridItem?: GridItemProps;
    noInvoicesFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    invoiceSummaryCard?: InvoiceSummaryCardStyles;
    titleText?: TitleTypographyProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    actionDataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
}

export const openInvoicesStyles: OpenInvoicesStyles = {
    wrapper: {
        ...dashboardTablesContainerStyles,
    },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    errorGridItem: { width: 12 },
    noInvoicesFoundGridItem: {
        width: 12,
        css: css`
            span[role="status"] {
                max-width: 100%;
                width: 100%;
                margin: 0;
            }
        `,
    },
    noInvoicesFoundText: {
        ...getNotifyStyle("info"),
    },
    titleText: {
        extendedStyles: {
            title: {
                color: "common.ui11"
            },
            link: {
                typographyProps: {
                    css: css`
                        line-height: 26px;
                    `,
                },
            }
        }
    },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
    dataTableHeaders: {
        css: css`
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    actionDataTableHeaders: {
        css: css`
            border-left: 1px solid ${color("common.ui8")};

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    dataTableBody: {
        css: css`
            ${xScreen("gte", "lg")} {
                tr:nth-child(even):hover .payInvoiceCell.payInvoiceCell.payInvoiceCell.payInvoiceCell {
                    background-color: ${color("common.ui12")};
                }
            }
            ${xScreen("lte", "md")} {
                display: flex;
                flex-wrap: wrap;

                tr:hover td:first-child {
                    background: none;
                }
            }
        `,
    },
    dataTableRow: {
        css: css`
            ${xScreen("gte", "lg")} {
                &:hover {
                    background: none;
                    td:not(.payInvoiceCell) {
                        background-color: ${color("common.ui1", 0.15)};
                    }
                }
            }
            ${xScreen("lte", "md")} {
                display: none;
                border-top: 4px solid ${color("common.ui11")};
                flex-direction: column;
                width: 50%;

                &.openInvoice-0,
                &.openInvoice-1 {
                    display: flex;
                }
            }

            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

const styles = openInvoicesStyles;

const openInvoicesParameter: GetInvoicesApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "invoiceDate DESC",
    showOpenOnly: true,
};

const OpenInvoices: FC<Props> = ({
    invoicesDataView,
    invoicesHistoryPageLink,
    invoiceDetailsPageLink,
    canViewInvoices,
    loadInvoices,
    fields: { wrapperBorders, numberOfRecords, payInvoicesLink }
}) => {
    useEffect(() => {
        openInvoicesParameter.pageSize = numberOfRecords;
        if (canViewInvoices && !invoicesDataView.value) {
            loadInvoices(openInvoicesParameter);
        }
    }, []);

    const payInvoicesUrl = useGetLink(payInvoicesLink)?.url;
    if (!canViewInvoices || !invoicesDataView.value) {
        return null;
    }

    const invoicesHistoryUrl = invoicesHistoryPageLink ? invoicesHistoryPageLink.url : undefined;
    const totalCount = invoicesDataView.pagination?.totalItemCount;

    return (
        <HistoryContext.Consumer>
            {({ history }) => {
                return (
                    <StyledWrapper {...wrapperBorders && { ...styles.wrapper }}>
                        <CardList
                            extendedStyles={styles.cardList}
                            data-test-selector="widget_OpenInvoices"
                        >
                            <TitleTypography
                                {...styles.titleText}
                                linkText={translate("View All")}
                                link={invoicesHistoryUrl}
                                iconProps={{
                                    size: 18,
                                    src: IconArrowRight
                                }}
                            >
                                {translate(`Open Invoices (${totalCount})`)}
                            </TitleTypography>
                            {invoicesDataView?.value?.length === 0 && (
                                <GridItem className="no-invoices-found-grid-item" {...styles.noInvoicesFoundGridItem}>
                                    <Typography
                                        className="no-invoices-found-text"
                                        {...styles.noInvoicesFoundText}
                                        data-test-selector="openInvoices_NoInvoicesFound"
                                    >
                                        {translate("No invoices found")}
                                    </Typography>
                                </GridItem>
                            )}
                            {invoicesDataView?.value?.length !== 0 && (
                                <StyledWrapper {...styles.tableWrapper}>
                                    <DataTable {...styles.dataTable}>
                                        <DataTableHead {...styles.dataTableHead}>
                                            <DataTableHeader tight {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                                            <DataTableHeader tight {...styles.dataTableHeaders}>{translate("Invoice #")}</DataTableHeader>
                                            <DataTableHeader tight {...styles.dataTableHeaders}>
                                                {translate("Ship-To / Pick Up")}
                                            </DataTableHeader>
                                            <DataTableHeader tight {...styles.dataTableHeaders}>{translate("Due Date")}</DataTableHeader>
                                            <DataTableHeader tight {...styles.dataTableHeaders}>{translate("Total Ammount")}</DataTableHeader>
                                            {payInvoicesUrl && (
                                                <DataTableHeader tight {...styles.actionDataTableHeaders}>{translate("Actions")}</DataTableHeader>
                                            )}
                                        </DataTableHead>
                                        <DataTableBody {...styles.dataTableBody}>
                                            {invoicesDataView?.value?.map(
                                                (invoice, index) =>
                                                    invoice !== null && (
                                                        <DataTableRow
                                                            {...styles.dataTableRow}
                                                            data-test-selector="openInvoice_dataLine"
                                                            className={`openInvoice-${index}`}
                                                            key={invoice.id}
                                                            onClick={() => {
                                                                if (invoicesHistoryUrl) {
                                                                    if (history) {
                                                                        history.push(`${invoiceDetailsPageLink}?invoiceNumber=${invoice.invoiceNumber}`);
                                                                    } else {
                                                                        window.location.href = `${invoiceDetailsPageLink}?invoiceNumber=${invoice.invoiceNumber}`;
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <InvoiceSummaryCard
                                                                invoice={invoice}
                                                                payInvoicesUrl={payInvoicesUrl}
                                                                extendedStyles={styles.invoiceSummaryCard}
                                                            />
                                                        </DataTableRow>
                                                    )
                                            )}
                                        </DataTableBody>
                                    </DataTable>
                                </StyledWrapper>
                            )}
                        </CardList>
                    </StyledWrapper>
                )
            }}
        </HistoryContext.Consumer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Open Invoices Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.wrapperBorders,
                displayName: "Wrapper Borders",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.payInvoicesLink,
                displayName: "Pay Invoices Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Url", value: "" },
            },
        ],
    },
};

export default widgetModule;
